import React, { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    Stack,
    Typography
} from '@mui/material';
import {
    Add as AddIcon,
    CheckCircle as CheckCircleIcon,
    Close as CloseIcon,
    Description as DescriptionIcon
} from '@mui/icons-material';
import { initializeDB } from '../../config/db';
import { CSEP_STORE } from '../../config/constants';

/**
 * Modal for selecting CSEP in JHA Step 2
 * Options: Take Photo, Select Existing CSEP, Create New CSEP
 */
export default function CsepSelectionModal({
    open,
    onClose,
    onSelectCsep,
    onCreateNew,
    jhaData // JHA data for filtering and pre-filling
}) {
    const [csepList, setCsepList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedCsep, setSelectedCsep] = useState(null);

    // Load CSEP forms from IndexedDB
    useEffect(() => {
        if (open) {
            loadCsepForms();
        }
    }, [open]);

    const loadCsepForms = async () => {
        setLoading(true);
        try {
            const db = await initializeDB();
            if (db.objectStoreNames.contains(CSEP_STORE)) {
                const tx = db.transaction(CSEP_STORE, 'readonly');
                const store = tx.objectStore(CSEP_STORE);
                const allForms = await store.getAll();
                setCsepList(allForms || []);
            }
        } catch (e) {
            console.error('Error loading CSEP forms:', e);
        } finally {
            setLoading(false);
        }
    };

    // Filter CSEP forms by job/location and date (+7 days)
    const filteredCsepList = useMemo(() => {
        if (!csepList.length) return [];

        const now = new Date();
        const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);

        // Get JHA job code or location for matching
        const jhaJobCode = jhaData?.stepOne?.jobNumber || '';
        const jhaLocation = jhaData?.stepOne?.locationName || jhaData?.stepOne?.jobSiteAddress || '';

        return csepList
            .filter(csep => {
                // Only show submitted/completed forms
                if (csep.status !== 'submitted' && csep.status !== 'completed') {
                    return false;
                }

                // Check date within last 7 days
                const csepDate = csep.submittedAt || csep.updatedAt || csep.createdAt;
                if (csepDate) {
                    const csepDateTime = new Date(csepDate);
                    if (csepDateTime < sevenDaysAgo) {
                        return false;
                    }
                }

                // Match by job code or location
                const csepJobCode = csep.stepOne?.jobCode || '';
                const csepLocation = csep.stepOne?.location || csep.stepTwo?.confinedSpaceLocation || '';

                // If JHA has job code, match by job code
                if (jhaJobCode && csepJobCode) {
                    if (csepJobCode === jhaJobCode) return true;
                }

                // If JHA has location, match by location (partial match)
                if (jhaLocation && csepLocation) {
                    const jhaLocLower = jhaLocation.toLowerCase();
                    const csepLocLower = csepLocation.toLowerCase();
                    if (csepLocLower.includes(jhaLocLower) || jhaLocLower.includes(csepLocLower)) {
                        return true;
                    }
                }

                // If no job/location in JHA, show all recent
                if (!jhaJobCode && !jhaLocation) {
                    return true;
                }

                return false;
            })
            .sort((a, b) => {
                const dateA = new Date(a.submittedAt || a.updatedAt || a.createdAt || 0);
                const dateB = new Date(b.submittedAt || b.updatedAt || b.createdAt || 0);
                return dateB - dateA; // Newest first
            });
    }, [csepList, jhaData]);

    const formatDate = (dateString) => {
        if (!dateString) return '—';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            month: '2-digit',
            day: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const handleSelectCsep = (csep) => {
        setSelectedCsep(csep);
    };

    const handleConfirmSelect = () => {
        if (selectedCsep) {
            onSelectCsep(selectedCsep);
            onClose();
        }
    };

    const handleCreateNew = () => {
        onCreateNew();
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{ sx: { maxHeight: '85vh' } }}
        >
            <DialogTitle sx={{ pb: 1 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6">Confined Space Entry Permit</Typography>
                    <IconButton onClick={onClose} size="small">
                        <CloseIcon />
                    </IconButton>
                </Stack>
            </DialogTitle>

            <DialogContent dividers sx={{ p: 2 }}>
                {/* Action Button */}
                <Button
                    variant="contained"
                    color="success"
                    startIcon={<AddIcon />}
                    onClick={handleCreateNew}
                    fullWidth
                    sx={{ justifyContent: 'flex-start', py: 1.5, mb: 2 }}
                >
                    Create New Confined Space Entry Permit
                </Button>

                <Divider sx={{ my: 2 }}>
                    <Chip label="or select existing" size="small" />
                </Divider>

                {/* Existing CSEP List */}
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                    Recent CSEPs (last 7 days, matching job/location)
                </Typography>

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress size={32} />
                    </Box>
                ) : filteredCsepList.length === 0 ? (
                    <Card variant="outlined" sx={{ p: 2, textAlign: 'center', backgroundColor: '#fafafa' }}>
                        <Typography variant="body2" color="text.secondary">
                            No matching CSEPs found
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            Create a new one or take a photo
                        </Typography>
                    </Card>
                ) : (
                    <Stack spacing={1} sx={{ maxHeight: 300, overflowY: 'auto' }}>
                        {filteredCsepList.map((csep) => {
                            const isSelected = selectedCsep?.uuid === csep.uuid;
                            const location = csep.stepOne?.location || csep.stepTwo?.confinedSpaceLocation || 'No location';
                            const jobCode = csep.stepOne?.jobCode || '';
                            const supervisor = csep.stepOne?.entrySupervisorName || '';

                            return (
                                <Card
                                    key={csep.uuid}
                                    variant="outlined"
                                    onClick={() => handleSelectCsep(csep)}
                                    sx={{
                                        cursor: 'pointer',
                                        borderColor: isSelected ? 'primary.main' : 'divider',
                                        borderWidth: isSelected ? 2 : 1,
                                        backgroundColor: isSelected ? 'primary.50' : 'white',
                                        '&:hover': {
                                            borderColor: 'primary.main',
                                            backgroundColor: isSelected ? 'primary.50' : 'action.hover'
                                        }
                                    }}
                                >
                                    <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                                        <Stack direction="row" alignItems="center" spacing={1}>
                                            {isSelected ? (
                                                <CheckCircleIcon color="primary" />
                                            ) : (
                                                <DescriptionIcon color="disabled" />
                                            )}
                                            <Box sx={{ flex: 1 }}>
                                                <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
                                                    {location}
                                                </Typography>
                                                <Stack direction="row" spacing={2} flexWrap="wrap">
                                                    {jobCode && (
                                                        <Typography variant="caption" color="text.secondary">
                                                            Job: {jobCode}
                                                        </Typography>
                                                    )}
                                                    {supervisor && (
                                                        <Typography variant="caption" color="text.secondary">
                                                            Supervisor: {supervisor}
                                                        </Typography>
                                                    )}
                                                    <Typography variant="caption" color="text.secondary">
                                                        {formatDate(csep.submittedAt || csep.updatedAt)}
                                                    </Typography>
                                                </Stack>
                                            </Box>
                                            <Chip
                                                label="Submitted"
                                                size="small"
                                                color="success"
                                                sx={{ height: 20, fontSize: '0.7rem' }}
                                            />
                                        </Stack>
                                    </CardContent>
                                </Card>
                            );
                        })}
                    </Stack>
                )}
            </DialogContent>

            <DialogActions sx={{ px: 2, py: 1.5 }}>
                <Button onClick={onClose} color="inherit">
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={handleConfirmSelect}
                    disabled={!selectedCsep}
                >
                    Select CSEP
                </Button>
            </DialogActions>
        </Dialog>
    );
}
